import React, { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { ModalWrapper } from "../chatScreen/chatbar";

const PROFILE_TAGS = ["Friend", "Family", "Work", "Other"];

const ImageUploadModal = ({ open, onClose, onUpload, uploading, onImagesChange }) => {
  const [profileData, setProfileData] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [slots, setSlots] = useState([]);
  const fileInputRefs = useRef([]);
  const [localUploading, setLocalUploading] = useState(false);
  const [removeIndex, setRemoveIndex] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [slotTagLoading, setSlotTagLoading] = useState({});

  const fetchProfileImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const token = Cookies.get("auth_token");
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "1") {
        const latestProfileData = data?.data?.[0] || null;
        setProfileData(latestProfileData);
        if (latestProfileData && onImagesChange) {
          onImagesChange(latestProfileData);
        }
      }
    } catch {
      toast.error("Failed to load profile images");
    } finally {
      setLoadingImages(false);
    }
  }, [onImagesChange]);

  useEffect(() => {
    if (open) fetchProfileImages();
  }, [open, fetchProfileImages]);

  useEffect(() => {
    if (!profileData) return;

    const defaultUrl = profileData.default_profile_image ?? "";
    const maxImages = profileData.max_profile_images ?? 0;

    const tagged = Array.isArray(profileData.profile_images_by_tag)
      ? profileData.profile_images_by_tag
      : [];

    const imageMap = tagged.reduce((acc, it) => {
      if (!it || !it.image_url) return acc;
      const imageId = it.image_id || it.imageId || it.id || null;
      const key = imageId || it.image_url;
      const isNonDefault = it.image_url && it.image_url !== defaultUrl;
      if (!acc[key]) {
        acc[key] = {
          image_url: it.image_url,
          uploaded: Boolean(it.uploaded) || isNonDefault,
          image_id: imageId,
          isDefault: Number(it.default) === 1,
          tags: [],
        };
      }

      if (Array.isArray(it.tags)) {
        it.tags.forEach((t) => {
          if (t && !acc[key].tags.includes(t)) acc[key].tags.push(t);
        });
      }

      acc[key].uploaded = acc[key].uploaded || Boolean(it.uploaded) || isNonDefault;
      acc[key].isDefault = acc[key].isDefault || Number(it.default) === 1;
      return acc;
    }, {});

    const filledSlots = Object.values(imageMap)
      .filter((entry) => entry.uploaded && entry.image_url && entry.image_url !== defaultUrl)
      .map((entry) => ({
        file: null,
        previewUrl: null,
        existingUrl: `${process.env.REACT_APP_API_BASE_URL}${entry.image_url}`,
        imageId: entry.image_id,
        isDefault: Boolean(entry.isDefault),
        tags: entry.tags || [],
        initialTags: entry.tags ? [...entry.tags] : [],
      }));

    const emptyCount = Math.max(0, maxImages - filledSlots.length);
    const emptySlots = Array.from({ length: emptyCount }, () => ({
      file: null,
      previewUrl: null,
      existingUrl: null,
      isDefault: false,
      tags: [],
      initialTags: [],
    }));

    setSlots([...filledSlots, ...emptySlots]);
  }, [profileData]);

  useEffect(() => {
    return () => {
      slots.forEach((s) => {
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      });
    };
  }, [slots]);

  if (!open) return null;

  const maxImages = profileData?.max_profile_images ?? 0;

  const getAvailableTags = (slotIdx) => {
    const takenByOthers = slots.flatMap((s, i) => (i !== slotIdx ? s.tags : []));
    return PROFILE_TAGS.filter((t) => !takenByOthers.includes(t));
  };

  const setSlotFile = (idx, file) => {
    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
        return { ...s, file, previewUrl: file ? URL.createObjectURL(file) : null };
      })
    );
  };

  const clearSlotFile = (idx) => {
    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
        return { ...s, file: null, previewUrl: null };
      })
    );
  };

  const toggleSlotTag = (slotIdx, tag) => {
    const current = slots[slotIdx];
    const prevTags = current && Array.isArray(current.tags) ? [...current.tags] : [];
    const wasPresent = prevTags.includes(tag);

    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== slotIdx) return s;
        const next = s.tags.includes(tag) ? s.tags.filter((t) => t !== tag) : [...s.tags, tag];
        return { ...s, tags: next };
      })
    );

    if (current?.file) return;

    const newTags = prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag];
    const callRemove = wasPresent;
    const callSelect = !wasPresent;

    if (!callRemove && !callSelect) return;

    setSlotTagLoading((prev) => ({ ...prev, [slotIdx]: true }));

    (async () => {
      try {
        const token = Cookies.get("auth_token");
        if (!token) {
          toast.error("Session expired. Please log in again.");
          return (window.location.href = "/login");
        }

        if (callRemove) {
          const body = new URLSearchParams();
          if (current.imageId) body.set("image_id", current.imageId);

          const removingWholeImage = newTags.length === 0 && current.imageId;
          if (!removingWholeImage) {
            body.set("tag_remove", tag);
          }

          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/remove-profile-image`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
            body: body.toString(),
          });
          const data = await res.json();

          if (data?.status === "1") {
            if (removingWholeImage) {
              toast.success("Image removed");
              setSlots((prev) =>
                prev.map((s, i) =>
                  i === slotIdx
                    ? {
                        ...s,
                        existingUrl: null,
                        imageId: null,
                        isDefault: false,
                        tags: [],
                        initialTags: [],
                      }
                    : s
                )
              );
            } else {
              toast.success("Tag removed");
              setSlots((prev) =>
                prev.map((s, i) => (i === slotIdx ? { ...s, initialTags: [...newTags] } : s))
              );
            }
            await fetchProfileImages();
          } else {
            toast.error(data?.message || "Failed to remove tag/image");
            setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, tags: prevTags } : s)));
          }
        }

        if (callSelect) {
          if (!current?.imageId) {
            toast.error("Please upload the image first before adding tags.");
            setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, tags: prevTags } : s)));
            return;
          }

          const body = new URLSearchParams();
          body.set("image_id", current.imageId);
          body.set("tag_select", newTags.join(","));

          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/selectprofileimage`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
            body: body.toString(),
          });
          const data = await res.json();

          if (data?.status === "1") {
            toast.success("Tag added");
            setSlots((prev) =>
              prev.map((s, i) => (i === slotIdx ? { ...s, initialTags: [...newTags] } : s))
            );
            await fetchProfileImages();
          } else {
            toast.error(data?.message || "Failed to add tag");
            setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, tags: prevTags } : s)));
          }
        }
      } catch (err) {
        toast.error(err?.message || "Failed to update tag");
        setSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, tags: prevTags } : s)));
      } finally {
        setSlotTagLoading((prev) => {
          const copy = { ...prev };
          delete copy[slotIdx];
          return copy;
        });
      }
    })();
  };

  const handleUploadAll = async () => {
    const toUpload = slots.filter((s) => s.file && Array.isArray(s.tags) && s.tags.length > 0);
    const skipped = slots.filter((s) => s.file && (!s.tags || s.tags.length === 0)).length;

    if (toUpload.length === 0) {
      if (skipped > 0) toast.error("Select a tag for each image before uploading");
      return;
    }

    try {
      setLocalUploading(true);
      for (const slot of toUpload) {
        // eslint-disable-next-line no-await-in-loop
        await onUpload(slot.file, slot.tags);
      }
      if (skipped > 0) {
        toast("Some images were skipped. Select a tag to upload them.");
      }
      await fetchProfileImages();
    } finally {
      setLocalUploading(false);
    }
  };

  const removeSlotImage = async (idx) => {
    const slot = slots[idx];
    if (!slot) return;
    setRemoveIndex(idx);
  };

  const performRemoveImage = async (idx) => {
    const slot = slots[idx];
    if (!slot) {
      setRemoveIndex(null);
      return;
    }

    try {
      setRemoving(true);
      const token = Cookies.get("auth_token");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setRemoving(false);
        return (window.location.href = "/login");
      }

      const body = new URLSearchParams();
      if (slot.imageId) {
        body.set("image_id", slot.imageId);
      } else if (slot.tags && slot.tags.length > 0) {
        body.set("tag_remove", slot.tags.join(","));
      } else {
        toast.error("No image id or tag available to remove");
        setRemoving(false);
        setRemoveIndex(null);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/remove-profile-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: body.toString(),
      });
      const data = await res.json();
      if (data?.status === "1") {
        toast.success("Image removed");
        await fetchProfileImages();
        onClose();
      } else {
        toast.error(data?.message || "Failed to remove image");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to remove image");
    } finally {
      setRemoving(false);
      setRemoveIndex(null);
    }
  };

  const pendingCount = slots.filter((s) => s.file && Array.isArray(s.tags) && s.tags.length > 0).length;

  if (removeIndex !== null) {
    return (
      <ModalWrapper onClose={() => setRemoveIndex(null)}>
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <img src="../delete.png" alt="Remove" className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Remove this profile image?</h2>
          <p className="text-sm text-gray-600 mb-6">This will remove the image from your profile.</p>
        </div>
        <button
          className="w-full bg-red-600 text-white font-semibold py-3 rounded-[25px] hover:bg-red-700 transition disabled:opacity-50"
          onClick={() => performRemoveImage(removeIndex)}
          disabled={removing}
        >
          {removing ? "Removing..." : "Yes, Remove"}
        </button>
        <button className="w-full submit-button mt-3" onClick={() => setRemoveIndex(null)} disabled={removing}>
          Cancel
        </button>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClose={onClose} className="w-[400px] relative">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
        Profile Photos ({slots.filter((s) => s.existingUrl).length}/{maxImages})
      </h2>

      {loadingImages ? (
        <div className="space-y-3 py-2">
          {Array.from({ length: maxImages || 2 }).map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {slots.map((slot, idx) => {
            const availableTags = getAvailableTags(idx);
            const displayUrl = slot.previewUrl ?? slot.existingUrl;

            return (
              <div key={idx} className="border border-gray-100 rounded-xl p-3 space-y-2 bg-gray-50">
                <p className="text-xs font-semibold text-gray-600">
                  Photo {idx + 1}
                  {slot.isDefault ? " (Default)" : ""}
                </p>

                <input
                  ref={(el) => {
                    fileInputRefs.current[idx] = el;
                  }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setSlotFile(idx, f);
                    e.target.value = "";
                  }}
                />

                {displayUrl ? (
                  <div className="flex items-center gap-3 bg-white rounded-lg p-2">
                    <img
                      src={displayUrl}
                      alt={`Photo ${idx + 1}`}
                      className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    />
                    <span className="text-sm text-gray-500 truncate flex-1">
                      {slot.file ? slot.file.name : "Current photo"}
                    </span>
                    <button
                      type="button"
                      onClick={() => fileInputRefs.current[idx]?.click()}
                      className="text-xs text-[#6C45FF] font-medium whitespace-nowrap"
                    >
                      Change
                    </button>
                    {!slot.file && slot.existingUrl && (
                      <button
                        type="button"
                        onClick={() => removeSlotImage(idx)}
                        className="text-xs text-red-500 font-medium ml-3 whitespace-nowrap"
                      >
                        Remove
                      </button>
                    )}

                    {slot.file && (
                      <button
                        type="button"
                        onClick={() => clearSlotFile(idx)}
                        className="text-gray-400 hover:text-red-500 text-base leading-none ml-1"
                      >
                        x
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[idx]?.click()}
                    className="w-full border-2 border-dashed border-[#6C45FF] rounded-lg py-3 text-sm text-[#6C45FF] font-medium hover:bg-purple-50 transition"
                  >
                    + Choose Photo
                  </button>
                )}

                <div>
                  <p className="text-xs text-gray-500 mb-1">Tags</p>
                  <div className="flex gap-2 flex-wrap">
                    {availableTags.length > 0 ? (
                      availableTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleSlotTag(idx, tag)}
                          disabled={!!slotTagLoading[idx]}
                          className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                            slot.tags.includes(tag)
                              ? "bg-[#6C45FF] text-white border-[#6C45FF]"
                              : "bg-white text-gray-700 border-gray-200 hover:border-[#6C45FF]"
                          } ${slotTagLoading[idx] ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          {tag}
                        </button>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 italic">No tags available</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {pendingCount > 0 && (
            <button
              type="button"
              onClick={handleUploadAll}
              disabled={uploading || localUploading}
              className="submit-button w-full text-white font-semibold py-3 rounded-[25px] hover:bg-[#5535ee] transition disabled:opacity-50"
            >
              {uploading || localUploading ? "Uploading..." : `Upload Photo${pendingCount > 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      )}

      <button className="w-full submit-button mt-3" onClick={onClose}>
        Close
      </button>

      {(loadingImages || localUploading || removing || Object.keys(slotTagLoading).length > 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg z-50">
          <div className="flex flex-col items-center gap-3 px-6 py-4 rounded-lg ">
            <div className="h-8 w-8 border-4 border-gray-200 border-t-[#6C45FF] rounded-full animate-spin" />
            <p className="text-sm font-medium text-gray-700">
              {loadingImages
                ? "Loading images..."
                : localUploading
                ? "Uploading..."
                : removing
                ? "Removing..."
                : "Updating..."}
            </p>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default ImageUploadModal;
